import React, { useCallback, useState } from 'react';
import { Download, Mail, Search, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale/tr';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import {
  deleteNewsletterSubscriber,
  getNewsletterStats,
  getNewsletterSubscribers,
} from '../../services/admin/newsletter';
import { getApiErrorMessage } from '../../lib/utils';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const PAGE_SIZE = 50;

const NewsletterPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [statsData, listData] = await Promise.all([
        getNewsletterStats(),
        getNewsletterSubscribers({
          search: appliedSearch,
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
        }),
      ]);
      setStats(statsData);
      setSubscribers(listData.items || []);
      setTotal(listData.total || 0);
    } catch (error) {
      console.error('Error fetching newsletter subscribers:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: getApiErrorMessage(error, 'Abone listesi yüklenirken bir hata oluştu.'),
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [appliedSearch, page, toast]);

  useAdminAutoRefresh(fetchData, { interval: 5000, deps: [appliedSearch, page] });

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setAppliedSearch(searchQuery.trim());
  };

  const handleDelete = async (subscriber) => {
    if (!window.confirm(`${subscriber.email} adresini listeden silmek istiyor musunuz?`)) {
      return;
    }
    try {
      await deleteNewsletterSubscriber(subscriber.id);
      toast({
        title: 'Silindi',
        description: 'Abone listeden kaldırıldı.',
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      toast({
        title: 'Hata',
        description: 'Abone silinirken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const exportToCSV = () => {
    if (subscribers.length === 0) {
      toast({
        title: 'Liste boş',
        description: 'Dışa aktarılacak abone bulunamadı.',
        variant: 'destructive',
      });
      return;
    }

    const headers = ['E-posta', 'Kaynak', 'Abone Olma Tarihi'];
    const rows = subscribers.map((item) => [
      item.email,
      item.source || 'footer',
      format(new Date(item.subscribed_at), 'dd.MM.yyyy HH:mm', { locale: tr }),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulten-aboneleri-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Bülten Aboneleri</h1>
            <p className="text-gray-600 mt-1">
              Sitedeki &quot;Abone Ol&quot; formundan kayıt olan e-posta adreslerini görüntüleyin.
            </p>
          </div>
          <div className="admin-toolbar">
            <Button variant="outline" onClick={exportToCSV} disabled={subscribers.length === 0}>
              <Download size={18} className="mr-2" />
              CSV İndir
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">Toplam Abone</p>
              <Mail className="text-blue-600" size={20} />
            </div>
            <p className="text-3xl font-bold">{stats.active}</p>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">Kayıtlı E-posta</p>
              <Mail className="text-green-600" size={20} />
            </div>
            <p className="text-3xl font-bold">{stats.total}</p>
          </div>
        </div>

        <form onSubmit={handleSearch} className="admin-search-row flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="E-posta ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="outline">
            Ara
          </Button>
        </form>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto" />
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">E-posta</th>
                    <th className="px-4 py-3 text-left">Kaynak</th>
                    <th className="px-4 py-3 text-left">Abone Olma Tarihi</th>
                    <th className="px-4 py-3 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{subscriber.email}</td>
                      <td className="px-4 py-3 text-gray-600">{subscriber.source || 'footer'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {format(new Date(subscriber.subscribed_at), 'dd MMM yyyy HH:mm', {
                          locale: tr,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(subscriber)}
                          aria-label="Aboneyi sil"
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {subscribers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        Henüz abone yok. Footer&apos;daki &quot;Abone Ol&quot; formundan ilk kayıt geldiğinde burada görünür.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-sm text-gray-600">
                  Toplam {total} abone · Sayfa {page + 1} / {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  >
                    Önceki
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page + 1 >= totalPages}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Sonraki
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default NewsletterPage;
